import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useUsage } from './use-usage';
import { useSubscription } from './use-subscription';

/**
 * Shows toast alerts when usage hits 80% and 100% of limits.
 * Tracks which alerts have already been shown to avoid spamming.
 */
export function useUsageAlerts() {
  const { data: usage } = useUsage();
  const { data: subscription } = useSubscription();
  const shownAlerts = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!usage) return;

    const checks = [
      {
        key: 'evaluations',
        used: usage.evaluations_used,
        limit: usage.monthly_evaluation_limit,
        label: 'AI Evaluations',
      },
      {
        key: 'transcriptions',
        used: usage.transcriptions_used,
        limit: usage.monthly_transcription_limit,
        label: 'Transcriptions',
      },
      {
        key: 'repairs',
        used: usage.repairs_used,
        limit: usage.monthly_repair_limit,
        label: 'Field Repairs',
      },
    ];

    const tier = usage.tier || 'free';

    for (const check of checks) {
      if (check.limit === 0) continue;
      const pct = check.used / check.limit;

      // 100% alert
      const fullKey = `${check.key}-100`;
      if (pct >= 1 && !shownAlerts.current.has(fullKey)) {
        shownAlerts.current.add(fullKey);
        if (tier === 'free') {
          toast.error(`${check.label} limit reached`, {
            description: 'Upgrade to Pro for 10× lower pricing and overage access.',
            duration: 10000,
            action: {
              label: 'Upgrade',
              onClick: () => window.location.assign('/pricing'),
            },
          });
        } else if (tier === 'pro') {
          toast.warning(`${check.label} included credits used`, {
            description: `Overage billing active at your Pro rate. Upgrade to Enterprise for 7× pricing.`,
            duration: 8000,
          });
        } else {
          toast.info(`${check.label} included credits used`, {
            description: `Overage billing active at your Enterprise 7× rate. No caps — unlimited usage.`,
            duration: 6000,
          });
        }
      }

      // 80% warning
      const warnKey = `${check.key}-80`;
      if (pct >= 0.8 && pct < 1 && !shownAlerts.current.has(warnKey)) {
        shownAlerts.current.add(warnKey);
        const remaining = check.limit - check.used;
        toast.warning(`${check.label}: ${remaining} credits remaining`, {
          description: tier === 'free'
            ? 'Running low — upgrade to Pro for 10× more credits.'
            : `${Math.round(pct * 100)}% of included credits used this period.`,
          duration: 6000,
          ...(tier === 'free' ? {
            action: {
              label: 'View Plans',
              onClick: () => window.location.assign('/pricing'),
            },
          } : {}),
        });
      }
    }
  }, [usage, subscription]);
}
